import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useCounter(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const FAQS = [
  { q: "How does the Chrome Extension work?", a: "Install the Prorately extension, open any quoting platform, and it automatically detects form fields and fills them using your client's stored data. It works across 40+ major insurance carrier portals." },
  { q: "Is my client data secure?", a: "Absolutely. All data is encrypted in transit and at rest using AES-256. We're SOC 2 Type II certified and fully HIPAA compliant for health insurance workflows." },
  { q: "Can I import my existing client list?", a: "Yes — upload a CSV or connect directly from your existing CRM like Salesforce, HubSpot, or AgencyZoom. Most agents are fully imported in under 5 minutes." },
  { q: "What quoting platforms are supported?", a: "We support 40+ platforms including EZLynx, Applied Epic, Hawksoft, QQ Catalyst, and all major direct carrier portals. New integrations are added weekly." },
  { q: "Is there a long-term contract?", a: "No contracts, ever. Start monthly, cancel anytime. Annual plans come with 2 months free and priority onboarding." },
];

const FEATURES = [
  { icon: "⚡", title: "Instant Autofill", desc: "Prorately reads your client profile and fills every field on any quoting form in under 3 seconds. Zero copy-paste, zero typos.", color: "#3B82F6" },
  { icon: "🧠", title: "Smart Client CRM", desc: "A purpose-built CRM for insurance agents. Store drivers, vehicles, properties, prior coverage, and every policy detail — all searchable instantly.", color: "#8B5CF6" },
  { icon: "🔄", title: "Multi-Carrier Sync", desc: "Quote across 40+ carriers simultaneously. One client record, infinite quoting sessions — all tracked and timestamped.", color: "#06B6D4" },
  { icon: "📊", title: "Pipeline Dashboard", desc: "See every lead, quote, and policy in one live view. Know exactly where each client is in your sales pipeline at a glance.", color: "#10B981" },
  { icon: "🔔", title: "Renewal Alerts", desc: "Automatic reminders 90, 60, and 30 days before every policy renewal. Never lose a client to a competitor because you forgot to call.", color: "#F59E0B" },
  { icon: "📱", title: "Mobile Ready", desc: "Full-featured mobile app so you can pull up a client file, add notes, or check your pipeline from anywhere — even between appointments.", color: "#EF4444" },
];

const TESTIMONIALS = [
  { name: "Sarah Mitchell", role: "Independent Agent, Texas", avatar: "SM", avatarBg: "#3B82F6", quote: "I was spending 45 minutes per quote manually entering data. Now it takes 4 minutes. I've written 30% more policies this quarter alone." },
  { name: "James Kowalski", role: "Agency Owner, Ohio", avatar: "JK", avatarBg: "#8B5CF6", quote: "We onboarded our 8-agent team in a single afternoon. The Chrome Extension just works — my team stopped complaining about data entry immediately." },
  { name: "Priya Nair", role: "Senior Broker, California", avatar: "PN", avatarBg: "#10B981", quote: "The renewal alert system alone has saved three accounts that would have lapsed. The ROI on this tool is absurd — it pays for itself in week one." },
];

const STEPS = [
  { num: "01", title: "Install the Extension", desc: "Add Prorately to Chrome in one click. Takes 30 seconds." },
  { num: "02", title: "Import Your Clients", desc: "Upload a CSV or connect your existing CRM. Your data appears instantly." },
  { num: "03", title: "Open Any Quote Form", desc: "Navigate to any carrier portal or quoting platform as you normally would." },
  { num: "04", title: "Watch It Fill Itself", desc: "Prorately detects the form and autofills every field. You just review and submit." },
];

export default function Home() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [statsRef, statsInView] = useInView(0.3);

  const agents   = useCounter(12400, 2200, statsInView);
  const quotes   = useCounter(98,    2000, statsInView);
  const timeSaved= useCounter(47,    1800, statsInView);
  const carriers = useCounter(40,    1600, statsInView);

  const particles = useRef(
    Array.from({ length: 18 }, (_, i) => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 3,
      delay: i * 0.4,
      duration: 6 + Math.random() * 6,
    }))
  ).current;

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#F8FAFF', color: '#0F172A', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        :root { --blue:#2563EB; --blue-light:#3B82F6; --blue-pale:#EFF6FF; --navy:#0F172A; --border:#E2E8F0; }
        html { scroll-behavior:smooth; }
        .sora { font-family:'Sora',sans-serif; }
        .dm   { font-family:'DM Sans',sans-serif; }

        /* ── NAV ── */
        .nav { position:sticky; top:0; z-index:100; background:rgba(255,255,255,0.92); backdrop-filter:blur(16px); border-bottom:1px solid var(--border); padding:0 2rem; height:68px; display:flex; align-items:center; justify-content:space-between; }
        .nav-logo { font-family:'Sora',sans-serif; font-size:1.25rem; font-weight:800; color:var(--blue); letter-spacing:-0.5px; text-decoration:none; display:flex; align-items:center; gap:0.4rem; }
        .nav-links a { color:#475569; font-size:0.9rem; font-weight:500; text-decoration:none; margin-left:2rem; transition:color 0.2s; }
        .nav-links a:hover { color:var(--blue); }
        .btn-nav { background:var(--blue); color:#fff; padding:0.55rem 1.4rem; border-radius:8px; font-size:0.9rem; font-weight:600; text-decoration:none; transition:background 0.2s,transform 0.15s; border:none; cursor:pointer; }
        .btn-nav:hover { background:#1D4ED8; transform:translateY(-1px); }
        .hamburger { display:none; background:none; border:1px solid var(--border); border-radius:8px; padding:0.4rem 0.6rem; cursor:pointer; font-size:1.2rem; }
        @media (max-width:768px) {
          .nav-links, .nav-desktop-cta { display:none !important; }
          .hamburger { display:block; }
          .mobile-menu { position:fixed; top:68px; left:0; right:0; background:#fff; border-bottom:1px solid var(--border); padding:1.5rem 2rem; z-index:99; box-shadow:0 8px 32px rgba(0,0,0,0.08); }
          .mobile-menu a { display:block; padding:0.75rem 0; color:#0F172A; font-size:1rem; font-weight:500; text-decoration:none; border-bottom:1px solid #f1f5f9; }
          .mobile-menu a:last-child { border-bottom:none; }
          .hero-btns { flex-direction:column !important; align-items:center !important; }
          .hero-btns a, .hero-btns button { width:100%; max-width:320px; text-align:center; }
        }

        /* ── HERO ── */
        .hero { position:relative; min-height:92vh; display:flex; align-items:center; overflow:hidden; background:#F8FAFF; }
        .hero-bg { position:absolute; inset:0; z-index:0; background:radial-gradient(ellipse 60% 80% at 0% 50%,#DBEAFE 0%,transparent 65%),radial-gradient(ellipse 40% 50% at 10% 80%,#EDE9FE 0%,transparent 55%),#F8FAFF; }
        .hero-grid { position:absolute; inset:0; z-index:0; background-image:linear-gradient(rgba(37,99,235,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(37,99,235,0.04) 1px,transparent 1px); background-size:40px 40px; }
        .particle { position:absolute; border-radius:50%; background:rgba(37,99,235,0.15); animation:float linear infinite; pointer-events:none; }
        @keyframes float { 0%{transform:translateY(0) scale(1);opacity:0.6} 50%{transform:translateY(-30px) scale(1.2);opacity:1} 100%{transform:translateY(0) scale(1);opacity:0.6} }

        .hero-split { position:relative; z-index:1; max-width:1200px; margin:0 auto; width:100%; padding:6rem 2rem; display:grid; grid-template-columns:1fr 1fr; gap:4rem; align-items:center; }
        @media (max-width:900px) {
          .hero-split { grid-template-columns:1fr; gap:3rem; padding:4rem 2rem; }
          .hero-img-col { order:-1; }
          .hero-content { text-align:center; }
          .hero-trust { justify-content:center !important; }
          .hero-btns  { justify-content:center !important; }
          .hero-badge { margin-left:auto; margin-right:auto; }
        }

        .hero-badge { display:inline-flex; align-items:center; gap:0.5rem; background:#EFF6FF; border:1px solid #BFDBFE; color:#1D4ED8; font-size:0.78rem; font-weight:600; padding:0.4rem 1rem; border-radius:100px; margin-bottom:2rem; letter-spacing:0.02em; }
        .badge-dot  { width:6px; height:6px; background:#2563EB; border-radius:50%; animation:pulse 2s infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

        .hero h1 { font-family:'Sora',sans-serif; font-size:clamp(2.2rem,4.5vw,3.8rem); font-weight:800; line-height:1.1; letter-spacing:-0.03em; color:#0F172A; margin-bottom:1.5rem; }
        .hero h1 em { font-style:normal; color:#2563EB; }
        .hero p { font-size:clamp(1rem,1.8vw,1.15rem); color:#64748B; max-width:500px; margin:0 0 2.5rem; line-height:1.75; }
        .hero-btns { display:flex; gap:1rem; flex-wrap:wrap; }
        .btn-primary { display:inline-flex; align-items:center; gap:0.5rem; background:#2563EB; color:#fff; padding:0.9rem 2rem; border-radius:12px; font-size:1rem; font-weight:700; text-decoration:none; box-shadow:0 8px 24px rgba(37,99,235,0.3); transition:all 0.2s; border:none; cursor:pointer; }
        .btn-primary:hover { background:#1D4ED8; transform:translateY(-2px); box-shadow:0 12px 32px rgba(37,99,235,0.4); }
        .btn-secondary { display:inline-flex; align-items:center; gap:0.5rem; background:#fff; color:#0F172A; padding:0.9rem 2rem; border-radius:12px; font-size:1rem; font-weight:600; text-decoration:none; border:1.5px solid #E2E8F0; transition:all 0.2s; cursor:pointer; }
        .btn-secondary:hover { border-color:#2563EB; color:#2563EB; transform:translateY(-2px); }
        .hero-trust { margin-top:2rem; color:#94A3B8; font-size:0.8rem; display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap; }
        .trust-sep { color:#CBD5E1; }

        /* Hero image column */
        .hero-img-col { position:relative; }
        .hero-img-frame { position:relative; border-radius:24px; overflow:hidden; box-shadow:0 32px 80px rgba(15,23,42,0.18),0 4px 16px rgba(37,99,235,0.1); aspect-ratio:4/5; max-height:580px; }
        .hero-img-frame img { width:100%; height:100%; object-fit:cover; object-position:center top; display:block; }
        .hero-img-frame::after { content:''; position:absolute; bottom:0; left:0; right:0; height:35%; background:linear-gradient(to top,rgba(248,250,255,0.6) 0%,transparent 100%); }
        .hero-float-badge { position:absolute; bottom:28px; left:-20px; background:#fff; border:1px solid #E2E8F0; border-radius:14px; padding:0.85rem 1.1rem; box-shadow:0 8px 32px rgba(15,23,42,0.12); display:flex; align-items:center; gap:0.75rem; z-index:2; }
        .float-badge-icon { width:38px; height:38px; background:#EFF6FF; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:1.1rem; flex-shrink:0; }
        .float-badge-num  { font-family:'Sora',sans-serif; font-weight:800; font-size:1.1rem; color:#0F172A; line-height:1; }
        .float-badge-sub  { font-size:0.72rem; color:#94A3B8; margin-top:0.1rem; }
        .hero-float-badge2 { position:absolute; top:28px; right:-20px; background:#2563EB; border-radius:14px; padding:0.85rem 1.1rem; box-shadow:0 8px 32px rgba(37,99,235,0.35); display:flex; align-items:center; gap:0.75rem; z-index:2; }
        .float-badge2-num { font-family:'Sora',sans-serif; font-weight:800; font-size:1rem; color:#fff; line-height:1; }
        .float-badge2-sub { font-size:0.7rem; color:rgba(255,255,255,0.7); margin-top:0.1rem; }
        @media (max-width:640px) { .hero-float-badge { left:8px; bottom:16px; } .hero-float-badge2 { right:8px; top:16px; } }

        /* ── PRODUCT PREVIEW ── */
        .product-preview-section { background:#F8FAFF; padding:0 2rem 6rem; }
        .preview-wrap { max-width:960px; margin:0 auto; }
        .preview-intro { text-align:center; margin-bottom:2.5rem; }
        .preview-intro p { color:#64748B; font-size:0.92rem; margin-top:0.5rem; }
        .preview-shell { background:#fff; border:1px solid #E2E8F0; border-radius:16px; box-shadow:0 24px 80px rgba(15,23,42,0.12),0 4px 16px rgba(37,99,235,0.08); overflow:hidden; }
        .preview-topbar { background:#F8FAFF; border-bottom:1px solid #E2E8F0; padding:0.75rem 1rem; display:flex; align-items:center; gap:0.5rem; }
        .tbar-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
        .preview-url-bar { flex:1; background:#E2E8F0; height:22px; border-radius:5px; margin-left:8px; display:flex; align-items:center; padding-left:10px; font-size:0.7rem; color:#94A3B8; }
        .preview-ext-badge { margin-left:8px; background:#2563EB; color:#fff; border-radius:6px; padding:0.2rem 0.6rem; font-size:0.65rem; font-weight:700; white-space:nowrap; }
        .preview-body { padding:1.5rem; display:flex; gap:1.5rem; }
        .preview-sidebar { width:180px; flex-shrink:0; background:#F8FAFF; border-radius:10px; padding:1rem; }
        .preview-sidebar-label { font-size:0.65rem; font-weight:700; color:#94A3B8; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:0.75rem; }
        .preview-sidebar-item { font-size:0.78rem; font-weight:600; color:#94A3B8; padding:0.5rem 0.75rem; border-radius:6px; margin-bottom:0.25rem; cursor:pointer; }
        .preview-sidebar-item.active { background:#EFF6FF; color:#2563EB; }
        .preview-main { flex:1; }
        .preview-card { background:#fff; border:1px solid #E2E8F0; border-radius:10px; padding:1rem 1.25rem; margin-bottom:0.75rem; }
        .preview-card-title { font-size:0.7rem; font-weight:700; color:#94A3B8; text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.75rem; }
        .preview-client-row { display:flex; align-items:center; gap:0.6rem; margin-bottom:0.75rem; }
        .preview-avatar { width:28px; height:28px; border-radius:50%; background:#2563EB; display:flex; align-items:center; justify-content:center; color:#fff; font-size:0.65rem; font-weight:700; flex-shrink:0; }
        .preview-client-name { font-size:0.78rem; font-weight:700; color:#0F172A; }
        .preview-client-sub  { font-size:0.65rem; color:#94A3B8; }
        .preview-tag { display:inline-block; font-size:0.65rem; font-weight:600; padding:0.2rem 0.5rem; border-radius:4px; background:#DCFCE7; color:#16A34A; margin-left:auto; }
        .preview-progress-row { display:flex; justify-content:space-between; font-size:0.7rem; color:#64748B; margin-bottom:0.4rem; }
        .preview-progress-row span:last-child { font-weight:700; color:#2563EB; }
        .preview-bar-bg { height:8px; background:#E2E8F0; border-radius:100px; overflow:hidden; }
        .preview-bar-fill { height:100%; width:87%; background:linear-gradient(90deg,#2563EB,#60A5FA); border-radius:100px; }
        .preview-field-row { display:flex; gap:0.5rem; margin-top:0.75rem; }
        .preview-field { flex:1; }
        .preview-field-label { font-size:0.62rem; color:#94A3B8; margin-bottom:0.2rem; }
        .preview-field-val { height:28px; border-radius:6px; border:1px solid #86EFAC; background:#F0FDF4; display:flex; align-items:center; padding:0 0.5rem; font-size:0.7rem; color:#16A34A; font-weight:600; justify-content:space-between; }
        .preview-field-val.empty { border-color:#E2E8F0; background:#F1F5F9; color:#94A3B8; font-weight:400; }
        @media (max-width:640px) {
          .preview-body { flex-direction:column; }
          .preview-sidebar { width:100%; display:flex; flex-wrap:wrap; gap:0.4rem; }
          .preview-sidebar-label { width:100%; }
        }

        /* ── STATS ── */
        .stats-section { background:#0F172A; padding:5rem 2rem; }
        .stats-inner { max-width:1000px; margin:0 auto; text-align:center; }
        .stats-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(180px,1fr)); gap:2rem; margin-top:3rem; }
        .stat-num { font-family:'Sora',sans-serif; font-size:clamp(2.5rem,5vw,3.5rem); font-weight:800; color:#fff; line-height:1; }
        .stat-num span { color:#3B82F6; }
        .stat-label { color:#94A3B8; font-size:0.9rem; margin-top:0.5rem; }
        .stats-label { font-family:'Sora',sans-serif; font-size:clamp(1.5rem,3vw,2rem); font-weight:700; color:#fff; }
        .stats-sub { color:#64748B; margin-top:0.5rem; }

        /* ── SECTIONS ── */
        section { padding:6rem 2rem; }
        .section-inner { max-width:1100px; margin:0 auto; }
        .section-tag { display:inline-block; background:#EFF6FF; color:#2563EB; font-size:0.75rem; font-weight:700; text-transform:uppercase; letter-spacing:0.08em; padding:0.35rem 0.9rem; border-radius:100px; border:1px solid #BFDBFE; margin-bottom:1rem; }
        .section-title { font-family:'Sora',sans-serif; font-size:clamp(1.8rem,3.5vw,2.6rem); font-weight:800; line-height:1.2; letter-spacing:-0.02em; color:#0F172A; margin-bottom:1rem; }
        .section-sub { color:#64748B; font-size:1.05rem; max-width:520px; line-height:1.7; }

        /* ── FEATURES ── */
        .features-section { background:#fff; }
        .features-header { text-align:center; margin-bottom:4rem; }
        .features-header .section-sub { margin:0 auto; }
        .features-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:1.5rem; }
        .feature-card { padding:2rem; border:1.5px solid #E2E8F0; border-radius:16px; background:#FAFBFE; transition:all 0.25s; cursor:default; }
        .feature-card:hover { border-color:#93C5FD; box-shadow:0 8px 32px rgba(37,99,235,0.1); transform:translateY(-3px); background:#fff; }
        .feature-icon  { width:52px; height:52px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:1.5rem; margin-bottom:1.25rem; }
        .feature-title { font-family:'Sora',sans-serif; font-weight:700; font-size:1.05rem; color:#0F172A; margin-bottom:0.6rem; }
        .feature-desc  { color:#64748B; font-size:0.92rem; line-height:1.65; }

        /* ── HOW IT WORKS ── */
        .how-section { background:#F8FAFF; }
        .how-layout { display:grid; grid-template-columns:1fr 1fr; gap:5rem; align-items:center; }
        @media (max-width:768px) { .how-layout { grid-template-columns:1fr; gap:3rem; } }
        .steps-list { display:flex; flex-direction:column; gap:1.5rem; }
        .step-item  { display:flex; gap:1.25rem; align-items:flex-start; }
        .step-num   { font-family:'Sora',sans-serif; font-size:0.75rem; font-weight:800; color:#2563EB; background:#EFF6FF; border:1.5px solid #BFDBFE; border-radius:8px; width:36px; height:36px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .step-title { font-weight:700; color:#0F172A; margin-bottom:0.25rem; }
        .step-desc  { color:#64748B; font-size:0.9rem; line-height:1.6; }

        /* Extension mockup */
        .ext-mockup { background:#fff; border:1px solid #E2E8F0; border-radius:16px; box-shadow:0 16px 60px rgba(15,23,42,0.1); overflow:hidden; }
        .ext-header { background:#2563EB; padding:1rem 1.25rem; display:flex; align-items:center; gap:0.75rem; }
        .ext-icon   { background:rgba(255,255,255,0.2); border-radius:8px; padding:0.4rem; font-size:1rem; }
        .ext-title  { color:#fff; font-weight:700; font-size:0.9rem; }
        .ext-body   { padding:1.25rem; }
        .ext-client-card { background:#F8FAFF; border:1px solid #E2E8F0; border-radius:10px; padding:0.9rem; margin-bottom:1rem; display:flex; align-items:center; gap:0.75rem; }
        .ext-avatar { width:36px; height:36px; border-radius:50%; background:#2563EB; display:flex; align-items:center; justify-content:center; color:#fff; font-weight:700; font-size:0.8rem; flex-shrink:0; }
        .ext-client-name { font-weight:700; font-size:0.85rem; color:#0F172A; }
        .ext-client-sub  { font-size:0.75rem; color:#94A3B8; }
        .ext-progress-label { display:flex; justify-content:space-between; font-size:0.75rem; font-weight:600; color:#475569; margin-bottom:0.4rem; }
        .ext-progress-bar  { height:8px; background:#E2E8F0; border-radius:100px; overflow:hidden; }
        .ext-progress-fill { height:100%; background:linear-gradient(90deg,#2563EB,#60A5FA); border-radius:100px; animation:progressAnim 2.5s ease-in-out infinite; }
        @keyframes progressAnim { 0%{width:20%} 60%{width:100%} 100%{width:100%} }
        .ext-fields { margin-top:1rem; display:flex; flex-direction:column; gap:0.5rem; }
        .ext-field  { flex:1; background:#fff; border:1px solid #E2E8F0; border-radius:6px; height:32px; display:flex; align-items:center; padding:0 0.6rem; font-size:0.72rem; color:#475569; }
        .ext-field.filled { border-color:#86EFAC; background:#F0FDF4; }
        .ext-field-label { font-size:0.65rem; color:#94A3B8; margin-bottom:0.2rem; }
        .ext-check  { color:#16A34A; font-size:0.75rem; margin-left:auto; }

        /* ── TESTIMONIALS ── */
        .test-section { background:#fff; }
        .test-header  { text-align:center; margin-bottom:3.5rem; }
        .test-header .section-sub { margin:0 auto; }
        .test-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:1.5rem; }
        .test-card { padding:2rem; background:#F8FAFF; border:1.5px solid #E2E8F0; border-radius:16px; transition:all 0.25s; }
        .test-card:hover { border-color:#93C5FD; box-shadow:0 8px 32px rgba(37,99,235,0.08); transform:translateY(-3px); }
        .test-stars  { color:#F59E0B; font-size:0.9rem; margin-bottom:1rem; letter-spacing:2px; }
        .test-quote  { color:#334155; font-size:0.95rem; line-height:1.7; margin-bottom:1.5rem; font-style:italic; }
        .test-author { display:flex; align-items:center; gap:0.75rem; }
        .test-avatar { width:40px; height:40px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-weight:700; font-size:0.85rem; flex-shrink:0; }
        .test-name { font-weight:700; font-size:0.9rem; color:#0F172A; }
        .test-role { font-size:0.78rem; color:#94A3B8; }

        /* ── PRICING ── */
        .pricing-section { background:#F8FAFF; }
        .pricing-header { text-align:center; margin-bottom:3.5rem; }
        .pricing-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(270px,1fr)); gap:1.5rem; max-width:900px; margin:0 auto; }
        .pricing-card { background:#fff; border:1.5px solid #E2E8F0; border-radius:20px; padding:2.5rem 2rem; transition:all 0.25s; }
        .pricing-card.featured { border-color:#2563EB; box-shadow:0 12px 48px rgba(37,99,235,0.15); position:relative; }
        .featured-badge { position:absolute; top:-14px; left:50%; transform:translateX(-50%); background:#2563EB; color:#fff; font-size:0.72rem; font-weight:700; padding:0.3rem 1rem; border-radius:100px; white-space:nowrap; }
        .pricing-name   { font-family:'Sora',sans-serif; font-weight:700; color:#0F172A; font-size:1rem; margin-bottom:0.5rem; }
        .pricing-price  { font-family:'Sora',sans-serif; font-size:3rem; font-weight:800; color:#0F172A; line-height:1; margin:1rem 0 0.25rem; }
        .pricing-price sub { font-size:1rem; font-weight:500; color:#94A3B8; vertical-align:bottom; }
        .pricing-price sup { font-size:1.25rem; font-weight:700; vertical-align:top; margin-top:0.5rem; display:inline-block; }
        .pricing-period  { color:#94A3B8; font-size:0.85rem; margin-bottom:1.5rem; }
        .pricing-divider { border:none; border-top:1px solid #E2E8F0; margin:1.5rem 0; }
        .pricing-features { list-style:none; display:flex; flex-direction:column; gap:0.75rem; margin-bottom:2rem; }
        .pricing-features li { font-size:0.88rem; color:#475569; display:flex; align-items:center; gap:0.6rem; }
        .check { color:#2563EB; font-weight:700; flex-shrink:0; }
        .pricing-cta { display:block; text-align:center; padding:0.85rem 1.5rem; border-radius:10px; font-weight:700; font-size:0.95rem; text-decoration:none; transition:all 0.2s; }
        .pricing-cta.primary   { background:#2563EB; color:#fff; box-shadow:0 4px 16px rgba(37,99,235,0.25); }
        .pricing-cta.primary:hover { background:#1D4ED8; transform:translateY(-1px); }
        .pricing-cta.secondary { background:#F8FAFF; color:#2563EB; border:1.5px solid #BFDBFE; }
        .pricing-cta.secondary:hover { background:#EFF6FF; }

        /* ── FAQ ── */
        .faq-section { background:#fff; }
        .faq-inner  { max-width:720px; margin:0 auto; }
        .faq-header { text-align:center; margin-bottom:3rem; }
        .faq-item   { border-bottom:1px solid #E2E8F0; padding:1.5rem 0; }
        .faq-question { display:flex; justify-content:space-between; align-items:center; cursor:pointer; gap:1rem; }
        .faq-q-text   { font-weight:600; color:#0F172A; font-size:0.98rem; }
        .faq-chevron  { color:#94A3B8; font-size:1rem; transition:transform 0.25s; flex-shrink:0; }
        .faq-chevron.open { transform:rotate(180deg); color:#2563EB; }
        .faq-answer { color:#64748B; font-size:0.92rem; line-height:1.7; max-height:0; overflow:hidden; transition:max-height 0.35s ease, padding-top 0.25s; }
        .faq-answer.open { max-height:200px; padding-top:0.75rem; }

        /* ── CTA ── */
        .cta-section { background:linear-gradient(135deg,#1E40AF 0%,#2563EB 50%,#3B82F6 100%); padding:6rem 2rem; text-align:center; position:relative; overflow:hidden; }
        .cta-bg { position:absolute; inset:0; background:radial-gradient(circle at 30% 50%,rgba(255,255,255,0.08) 0%,transparent 60%),radial-gradient(circle at 70% 50%,rgba(255,255,255,0.06) 0%,transparent 60%); }
        .cta-content { position:relative; z-index:1; max-width:640px; margin:0 auto; }
        .cta-title { font-family:'Sora',sans-serif; font-size:clamp(2rem,4vw,3rem); font-weight:800; color:#fff; line-height:1.2; margin-bottom:1rem; }
        .cta-sub   { color:rgba(255,255,255,0.75); font-size:1.05rem; margin-bottom:2.5rem; line-height:1.6; }
        .cta-btns  { display:flex; gap:1rem; justify-content:center; flex-wrap:wrap; }
        .btn-white { background:#fff; color:#2563EB; padding:0.9rem 2rem; border-radius:12px; font-weight:700; font-size:1rem; text-decoration:none; transition:all 0.2s; border:none; cursor:pointer; }
        .btn-white:hover { background:#F0F9FF; transform:translateY(-2px); box-shadow:0 8px 24px rgba(0,0,0,0.15); }
        .btn-outline-white { background:transparent; color:#fff; padding:0.9rem 2rem; border-radius:12px; font-weight:600; font-size:1rem; text-decoration:none; border:1.5px solid rgba(255,255,255,0.4); transition:all 0.2s; cursor:pointer; }
        .btn-outline-white:hover { background:rgba(255,255,255,0.1); transform:translateY(-2px); }

        /* ── FOOTER ── */
        footer { background:#0F172A; color:#94A3B8; padding:4rem 2rem 2rem; }
        .footer-inner { max-width:1100px; margin:0 auto; }
        .footer-top { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:3rem; margin-bottom:3rem; }
        @media (max-width:768px) { .footer-top { grid-template-columns:1fr 1fr; gap:2rem; } }
        @media (max-width:480px) { .footer-top { grid-template-columns:1fr; } }
        .footer-brand-name { font-family:'Sora',sans-serif; font-weight:800; font-size:1.15rem; color:#fff; margin-bottom:0.75rem; }
        .footer-brand-desc { font-size:0.88rem; line-height:1.6; color:#64748B; max-width:260px; }
        .footer-col-title  { color:#fff; font-weight:700; font-size:0.85rem; margin-bottom:1rem; letter-spacing:0.02em; }
        .footer-col a { display:block; color:#64748B; font-size:0.85rem; text-decoration:none; margin-bottom:0.6rem; transition:color 0.2s; }
        .footer-col a:hover { color:#93C5FD; }
        .footer-bottom { border-top:1px solid #1E293B; padding-top:1.5rem; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem; font-size:0.82rem; color:#475569; }
        .footer-bottom a { color:#475569; text-decoration:none; }
        .footer-bottom a:hover { color:#93C5FD; }
        .footer-compliance { display:flex; gap:1rem; flex-wrap:wrap; }
        .compliance-badge { background:#1E293B; border:1px solid #334155; color:#64748B; font-size:0.7rem; font-weight:600; padding:0.25rem 0.6rem; border-radius:5px; }
      `}</style>

      {/* ── NAV ── */}
      <nav className="nav">
        <Link to="/" className="nav-logo sora">⚡ Prorately</Link>
        <div className="nav-links" style={{ display:'flex', alignItems:'center' }}>
          <Link to="/features">Features</Link>
          <Link to="/how-it-works">How It Works</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/faq">FAQ</Link>
        </div>
        <div className="nav-desktop-cta" style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <Link to="/login" style={{ color:'#475569', fontWeight:500, textDecoration:'none', fontSize:'0.9rem' }}>Login</Link>
          <Link to="/register" className="btn-nav">Start Free Trial</Link>
        </div>
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      </nav>

      {mobileOpen && (
        <div className="mobile-menu">
          <Link to="/features"     onClick={() => setMobileOpen(false)}>Features</Link>
          <Link to="/how-it-works" onClick={() => setMobileOpen(false)}>How It Works</Link>
          <Link to="/pricing"      onClick={() => setMobileOpen(false)}>Pricing</Link>
          <Link to="/faq"          onClick={() => setMobileOpen(false)}>FAQ</Link>
          <Link to="/login"        onClick={() => setMobileOpen(false)}>Login</Link>
          <Link to="/register"     onClick={() => setMobileOpen(false)} style={{ color:'#2563EB', fontWeight:700 }}>Start Free Trial →</Link>
        </div>
      )}

      {/* ── HERO ── */}
      <div className="hero">
        <div className="hero-bg" />
        <div className="hero-grid" />
        {particles.map((p, i) => (
          <div key={i} className="particle" style={{ left:`${p.x}%`, top:`${p.y}%`, width:p.size, height:p.size, animationDuration:`${p.duration}s`, animationDelay:`${p.delay}s` }} />
        ))}
        <div className="hero-split">
          {/* Left — text */}
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-dot" />
              Trusted by 12,400+ Insurance Agents
            </div>
            <h1 className="sora">
              Quote faster.<br />Close more.<br /><em>Stress less.</em>
            </h1>
            <p className="dm">
              Prorately autofills your quotes across 40+ carrier portals using your client's data — so you spend time selling, not typing.
            </p>
            <div className="hero-btns">
              <Link to="/register" className="btn-primary">🚀 Start Free Trial — No Card Needed</Link>
              <button className="btn-secondary">▶ Watch 2-min Demo</button>
            </div>
            <div className="hero-trust">
              <span>✓ Free 14-day trial</span>
              <span className="trust-sep">·</span>
              <span>✓ No credit card required</span>
              <span className="trust-sep">·</span>
              <span>✓ Cancel anytime</span>
              <span className="trust-sep">·</span>
              <span>✓ SOC 2 Certified</span>
            </div>
          </div>

          {/* Right — agent photo */}
          <div className="hero-img-col">
            <div className="hero-float-badge2">
              <span style={{ fontSize:'1.1rem' }}>⚡</span>
              <div>
                <div className="float-badge2-num">3 sec</div>
                <div className="float-badge2-sub">avg. autofill time</div>
              </div>
            </div>
            <div className="hero-img-frame">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80&fit=crop&crop=top"
                alt="Professional insurance agent working at desk with laptop"
              />
            </div>
            <div className="hero-float-badge">
              <div className="float-badge-icon">📋</div>
              <div>
                <div className="float-badge-num">47 min</div>
                <div className="float-badge-sub">saved per client</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PRODUCT PREVIEW ── */}
      <div className="product-preview-section">
        <div className="preview-wrap">
          <div className="preview-intro">
            <span className="section-tag">See It In Action</span>
            <p className="dm">This is what your screen looks like while Prorately works in the background</p>
          </div>
          <div className="preview-shell">
            {/* Browser chrome */}
            <div className="preview-topbar">
              <div className="tbar-dot" style={{ background:'#FF5F57' }} />
              <div className="tbar-dot" style={{ background:'#FFBD2E' }} />
              <div className="tbar-dot" style={{ background:'#28C840' }} />
              <div className="preview-url-bar">🔒 app.prorately.io/quote</div>
              <div className="preview-ext-badge">Prorately ⚡</div>
            </div>
            {/* App body */}
            <div className="preview-body">
              {/* Sidebar */}
              <div className="preview-sidebar">
                <div className="preview-sidebar-label">Navigation</div>
                {['Dashboard', 'Clients', 'Quotes', 'Policies', 'Renewals'].map((item, i) => (
                  <div key={item} className={`preview-sidebar-item${i === 1 ? ' active' : ''}`}>{item}</div>
                ))}
              </div>
              {/* Main content */}
              <div className="preview-main">
                {/* Autofill status card */}
                <div className="preview-card">
                  <div className="preview-card-title">⚡ Prorately Autofill — Active</div>
                  <div className="preview-client-row">
                    <div className="preview-avatar">JD</div>
                    <div>
                      <div className="preview-client-name">James Donovan</div>
                      <div className="preview-client-sub">Auto + Home Bundle · EZLynx</div>
                    </div>
                    <div className="preview-tag">Filling…</div>
                  </div>
                  <div className="preview-progress-row">
                    <span>Autofilling fields</span>
                    <span>14 / 16</span>
                  </div>
                  <div className="preview-bar-bg">
                    <div className="preview-bar-fill" />
                  </div>
                </div>
                {/* Filled fields */}
                <div className="preview-card">
                  <div className="preview-card-title">Client Details — Autofilled</div>
                  <div className="preview-field-row">
                    {[
                      { label:'First Name',  val:'James' },
                      { label:'Last Name',   val:'Donovan' },
                      { label:'Date of Birth', val:'04/12/1985' },
                      { label:'ZIP Code',    val:'78201' },
                    ].map(f => (
                      <div className="preview-field" key={f.label}>
                        <div className="preview-field-label">{f.label}</div>
                        <div className="preview-field-val">
                          <span style={{ fontSize:'0.7rem' }}>{f.val}</span>
                          <span style={{ color:'#16A34A', fontSize:'0.7rem', marginLeft:'auto' }}>✓</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="preview-field-row" style={{ marginTop:'0.5rem' }}>
                    {[
                      { label:'Vehicle Year', val:'2021' },
                      { label:'Make / Model', val:'Ford F-150' },
                      { label:'Prior Carrier', val:'State Farm' },
                      { label:'Coverage',     val:'— detecting —', empty:true },
                    ].map(f => (
                      <div className="preview-field" key={f.label}>
                        <div className="preview-field-label">{f.label}</div>
                        <div className={`preview-field-val${f.empty ? ' empty' : ''}`}>
                          <span style={{ fontSize:'0.7rem' }}>{f.val}</span>
                          {!f.empty && <span style={{ color:'#16A34A', fontSize:'0.7rem', marginLeft:'auto' }}>✓</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="stats-section" ref={statsRef}>
        <div className="stats-inner">
          <div className="stats-label sora">Powering the modern insurance agent</div>
          <p className="stats-sub dm">Real numbers from real agents using Prorately every day</p>
          <div className="stats-grid">
            <div><div className="stat-num sora">{agents.toLocaleString()}<span>+</span></div><div className="stat-label">Agents Active</div></div>
            <div><div className="stat-num sora">{timeSaved}<span>min</span></div><div className="stat-label">Saved Per Quote</div></div>
            <div><div className="stat-num sora">{carriers}<span>+</span></div><div className="stat-label">Carrier Portals</div></div>
            <div><div className="stat-num sora">{quotes}<span>%</span></div><div className="stat-label">Field Accuracy</div></div>
          </div>
        </div>
      </div>

      {/* ── FEATURES ── */}
      <section id="features" className="features-section">
        <div className="section-inner">
          <div className="features-header">
            <span className="section-tag">Features</span>
            <h2 className="section-title sora">Everything you need to quote faster<br />and close more policies</h2>
            <p className="section-sub dm">Built specifically for independent agents and agency teams. Not a generic CRM — an insurance-first platform.</p>
          </div>
          <div className="features-grid">
            {FEATURES.map(f => (
              <div className="feature-card" key={f.title}>
                <div className="feature-icon" style={{ background:`${f.color}15` }}>{f.icon}</div>
                <div className="feature-title sora">{f.title}</div>
                <div className="feature-desc dm">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="how-section">
        <div className="section-inner">
          <div className="how-layout">
            <div>
              <span className="section-tag">How It Works</span>
              <h2 className="section-title sora">From install to<br />first autofill in under 5 minutes</h2>
              <p className="section-sub dm" style={{ marginBottom:'2.5rem' }}>No complicated setup. No training required. If you can use Chrome, you can use Prorately.</p>
              <div className="steps-list">
                {STEPS.map(s => (
                  <div className="step-item" key={s.num}>
                    <div className="step-num sora">{s.num}</div>
                    <div>
                      <div className="step-title sora">{s.title}</div>
                      <div className="step-desc dm">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:'2.5rem' }}>
                <Link to="/register" className="btn-primary" style={{ display:'inline-flex' }}>Get Started Free →</Link>
              </div>
            </div>
            {/* Extension mockup */}
            <div>
              <div className="ext-mockup" style={{ maxWidth:320, margin:'0 auto' }}>
                <div className="ext-header">
                  <div className="ext-icon">⚡</div>
                  <div>
                    <div className="ext-title">Prorately</div>
                    <div style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.72rem' }}>Chrome Extension</div>
                  </div>
                  <div style={{ marginLeft:'auto', background:'rgba(255,255,255,0.15)', borderRadius:6, padding:'0.2rem 0.5rem', color:'#fff', fontSize:'0.7rem', fontWeight:600 }}>● Active</div>
                </div>
                <div className="ext-body">
                  <div className="ext-client-card">
                    <div className="ext-avatar">PN</div>
                    <div>
                      <div className="ext-client-name">Priya Nair</div>
                      <div className="ext-client-sub">Auto · 2022 Toyota RAV4</div>
                    </div>
                  </div>
                  <div className="ext-progress-label">
                    <span>Filling form fields…</span>
                    <span style={{ color:'#2563EB' }}>87%</span>
                  </div>
                  <div className="ext-progress-bar"><div className="ext-progress-fill" /></div>
                  <div className="ext-fields">
                    {[
                      { label:'Vehicle Year',  val:'2022',        done:true  },
                      { label:'Make / Model',  val:'Toyota RAV4', done:true  },
                      { label:'Annual Mileage',val:'12,400',      done:true  },
                      { label:'Prior Coverage',val:'State Farm',  done:true  },
                      { label:'Coverage Limit',val:'— detecting —', done:false },
                    ].map(field => (
                      <div key={field.label}>
                        <div className="ext-field-label">{field.label}</div>
                        <div className={`ext-field${field.done ? ' filled' : ''}`}>
                          <span style={{ fontSize:'0.72rem', color:field.done ? '#16A34A' : '#94A3B8', fontWeight:field.done ? 600 : 400 }}>{field.val}</span>
                          {field.done && <span className="ext-check">✓</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="test-section">
        <div className="section-inner">
          <div className="test-header">
            <span className="section-tag">Testimonials</span>
            <h2 className="section-title sora">Agents love Prorately</h2>
            <p className="section-sub dm">Don't take our word for it — hear from agents who've transformed their workflow.</p>
          </div>
          <div className="test-grid">
            {TESTIMONIALS.map(t => (
              <div className="test-card" key={t.name}>
                <div className="test-stars">★★★★★</div>
                <p className="test-quote dm">"{t.quote}"</p>
                <div className="test-author">
                  <div className="test-avatar" style={{ background:t.avatarBg }}>{t.avatar}</div>
                  <div>
                    <div className="test-name sora">{t.name}</div>
                    <div className="test-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="pricing-section">
        <div className="section-inner">
          <div className="pricing-header">
            <span className="section-tag">Pricing</span>
            <h2 className="section-title sora">Simple, transparent pricing</h2>
            <p className="section-sub dm" style={{ margin:'0 auto' }}>No hidden fees. No per-carrier charges. Every plan includes the Chrome Extension and full CRM.</p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-name sora">Solo Agent</div>
              <div style={{ color:'#64748B', fontSize:'0.85rem' }}>For independent agents getting started</div>
              <div className="pricing-price sora"><sup>$</sup>29<sub>/mo</sub></div>
              <div className="pricing-period">billed monthly · or $24/mo annual</div>
              <hr className="pricing-divider" />
              <ul className="pricing-features">
                {['Chrome Extension autofill','Up to 500 clients','10 carrier portals','Basic CRM & pipeline','Email support'].map(f => (
                  <li key={f}><span className="check">✓</span>{f}</li>
                ))}
              </ul>
              <Link to="/register" className="pricing-cta secondary">Start Free Trial</Link>
            </div>

            <div className="pricing-card featured">
              <div className="featured-badge">⭐ Most Popular</div>
              <div className="pricing-name sora">Pro Agent</div>
              <div style={{ color:'#64748B', fontSize:'0.85rem' }}>For serious agents who want to dominate</div>
              <div className="pricing-price sora"><sup>$</sup>69<sub>/mo</sub></div>
              <div className="pricing-period">billed monthly · or $57/mo annual</div>
              <hr className="pricing-divider" />
              <ul className="pricing-features">
                {['Everything in Solo','Unlimited clients','40+ carrier portals','Advanced CRM + automation','Renewal alerts & reminders','Priority support','Team collaboration (up to 3)'].map(f => (
                  <li key={f}><span className="check">✓</span>{f}</li>
                ))}
              </ul>
              <Link to="/register" className="pricing-cta primary">Start Free Trial</Link>
            </div>

            <div className="pricing-card">
              <div className="pricing-name sora">Agency Team</div>
              <div style={{ color:'#64748B', fontSize:'0.85rem' }}>For growing agencies with multiple agents</div>
              <div className="pricing-price sora"><sup>$</sup>149<sub>/mo</sub></div>
              <div className="pricing-period">billed monthly · or $124/mo annual</div>
              <hr className="pricing-divider" />
              <ul className="pricing-features">
                {['Everything in Pro','Unlimited agents','Team dashboard & reporting','Manager permissions & roles','Custom integrations','Dedicated account manager','SOC 2 compliance docs'].map(f => (
                  <li key={f}><span className="check">✓</span>{f}</li>
                ))}
              </ul>
              <Link to="/register" className="pricing-cta secondary">Start Free Trial</Link>
            </div>
          </div>
          <p style={{ textAlign:'center', color:'#94A3B8', fontSize:'0.85rem', marginTop:'2rem' }}>
            All plans include a 14-day free trial. No credit card required. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="faq-section">
        <div className="faq-inner">
          <div className="faq-header">
            <span className="section-tag">FAQ</span>
            <h2 className="section-title sora">Questions? We've got answers.</h2>
          </div>
          {FAQS.map((faq, i) => (
            <div className="faq-item" key={i}>
              <div className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span className="faq-q-text sora">{faq.q}</span>
                <span className={`faq-chevron${openFaq === i ? ' open' : ''}`}>▾</span>
              </div>
              <div className={`faq-answer dm${openFaq === i ? ' open' : ''}`}>{faq.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="cta-section">
        <div className="cta-bg" />
        <div className="cta-content">
          <h2 className="cta-title sora">Ready to close more policies?</h2>
          <p className="cta-sub dm">Join 12,400+ agents who've cut quoting time by 47 minutes per client. Start your free 14-day trial — no card needed.</p>
          <div className="cta-btns">
            <Link to="/register" className="btn-white">Start Free Trial →</Link>
            <button className="btn-outline-white">Watch Demo ▶</button>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer>
        <div className="footer-inner">
          <div className="footer-top">
            <div>
              <div className="footer-brand-name">⚡ Prorately</div>
              <p className="footer-brand-desc">The AI-powered CRM and Chrome Extension built exclusively for insurance agents. Quote faster, close more, stress less.</p>
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'1.25rem', flexWrap:'wrap' }}>
                <span className="compliance-badge">SOC 2 Type II</span>
                <span className="compliance-badge">HIPAA Compliant</span>
                <span className="compliance-badge">AES-256 Encrypted</span>
              </div>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Product</div>
              <Link to="/features">Features</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/how-it-works">How It Works</Link>
              <a href="#">Changelog</a>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Company</div>
              <a href="#">About</a>
              <a href="#">Blog</a>
              <a href="#">Careers</a>
              <a href="#">Contact</a>
            </div>
            <div className="footer-col">
              <div className="footer-col-title">Legal</div>
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
              <a href="#">Security</a>
              <a href="#">Cookie Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} Prorately. All rights reserved.</span>
            <div className="footer-compliance">
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
              <a href="#">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}